import { create } from "zustand";
import type { Layout } from "react-grid-layout";

import type {
  DashboardByCodeResponse,
  DashboardWidgetDefinition,
} from "src/types/dashboard";
import type {
  BuilderCardTemplate,
  BuilderWidgetTemplate,
} from "src/components/builder/Sidebar";

export type BuilderSlotInfo = {
  totalSlots: number;
  filledSlots: number;
  emptySlots: number;
};

export type BuilderSaveLayoutDraftItem = {
  item_id: string;
  slot_template_id: string;
  assigned_widget_id: string | null;
  label: string;
  category: string;
  description: string;
  title_override: string | null;
  saved_filters: Record<string, string | string[]>;
  is_visible: boolean;
  chart_type: string;
  position: number;
  grid: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  grid_constraints: {
    min_w: number;
    min_h: number;
    max_w: number;
    max_h: number;
  };
};

export type BuilderSaveLayoutDraft = {
  dashboard_code: string | null;
  items: BuilderSaveLayoutDraftItem[];
};

export type BuilderCanvasItem = {
  i: string;
  templateId: string;
  label: string;
  category: string;
  description: string;
  titleOverride: string | null;
  savedFilters: Record<string, string | string[]>;
  isVisible: boolean;
  // accent: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW: number;
  minH: number;
  maxW: number;
  maxH: number;
  assignedWidgetId: string | null;
  persistedWidgetId: string | null;
  isServerWidgetAssignment: boolean;
};

type BuilderStore = {
  currentDashboard: DashboardByCodeResponse | null;
  selectedDashboardCode: string | null;
  dashboardTemplates: BuilderWidgetTemplate[];
  slotInfo: BuilderSlotInfo;
  canvasItems: BuilderCanvasItem[];
  saveLayoutDraft: BuilderSaveLayoutDraft;
  pendingWidgetDeletionIds: string[];
  nextCanvasItemId: number;
  draggingWidgetId: string | null;
  setCurrentDashboard: (dashboard: DashboardByCodeResponse | null) => void;
  setSelectedDashboardCode: (dashboardCode: string | null) => void;
  setDashboardTemplates: (templates: BuilderWidgetTemplate[]) => void;
  setSlotInfo: (slotInfo: { totalSlots: number; filledSlots: number }) => void;
  setDraggingWidgetId: (widgetId: string | null) => void;
  addCanvasItem: (template: BuilderCardTemplate) => void;
  addWidgetCanvasItem: (widgetId: string) => void;
  updateCanvasItemWidgetConfig: (
    itemId: string,
    config: {
      titleOverride?: string | null;
      savedFilters?: Record<string, string | string[]>;
    },
  ) => void;
  queueWidgetDeletion: (widgetId: string) => void;
  clearPendingWidgetDeletions: () => void;
  removeCanvasItem: (itemId: string) => void;
  commitCanvasLayout: (layout: Layout) => void;
  swapCanvasItems: (sourceItemId: string, targetItemId: string) => void;
  assignWidgetToCanvasItem: (itemId: string, widgetId: string) => void;
  resetCanvas: () => void;
  syncDashboard: (
    dashboard: DashboardByCodeResponse | null,
    totalSlots?: number,
  ) => void;
  setSlotsFromWidgets: (
    widgets: DashboardWidgetDefinition[],
    totalSlots?: number,
  ) => void;
  resetBuilderStore: () => void;
};

const EMPTY_SLOT_INFO: BuilderSlotInfo = {
  totalSlots: 0,
  filledSlots: 0,
  emptySlots: 0,
};

const EMPTY_SAVE_LAYOUT_DRAFT: BuilderSaveLayoutDraft = {
  dashboard_code: null,
  items: [],
};

const clampToNonNegative = (value: number): number => Math.max(0, value);

const clampBetween = (
  value: number,
  minValue: number,
  maxValue: number,
): number => Math.min(Math.max(value, minValue), maxValue);

const buildSlotInfo = (
  totalSlots: number,
  filledSlots: number,
): BuilderSlotInfo => {
  const normalizedTotalSlots = clampToNonNegative(totalSlots);
  const normalizedFilledSlots = Math.min(
    clampToNonNegative(filledSlots),
    normalizedTotalSlots,
  );

  return {
    totalSlots: normalizedTotalSlots,
    filledSlots: normalizedFilledSlots,
    emptySlots: normalizedTotalSlots - normalizedFilledSlots,
  };
};

const getSlotInfoFromWidgets = (
  widgets: DashboardWidgetDefinition[],
  totalSlots?: number,
): BuilderSlotInfo => {
  const filledSlots = widgets.length;
  const resolvedTotalSlots = totalSlots ?? filledSlots;

  return buildSlotInfo(resolvedTotalSlots, filledSlots);
};

const createCanvasItemFromWidget = (
  widget: DashboardWidgetDefinition,
  index: number,
): BuilderCanvasItem => ({
  i: `builder-widget-${widget.widget_template_id}-${index}`,
  templateId: String(widget.widget_template_id),
  label: widget.title,
  category: widget.chart_type,
  description: widget.title,
  titleOverride: null,
  savedFilters: {},
  isVisible: true,
  x: widget.grid.x,
  y: widget.grid.y,
  w: widget.grid.w,
  h: widget.grid.h,
  minW: widget.grid_constraints.min_w,
  minH: widget.grid_constraints.min_h,
  maxW: widget.grid_constraints.max_w,
  maxH: widget.grid_constraints.max_h,
  assignedWidgetId: String(widget.widget_template_id),
  persistedWidgetId:
    widget.id !== undefined && widget.id !== null ? String(widget.id) : null,
  isServerWidgetAssignment: true,
});

const createCanvasItemsFromWidgets = (
  widgets: DashboardWidgetDefinition[],
): BuilderCanvasItem[] =>
  widgets
    .slice()
    .sort((left, right) => left.position - right.position)
    .map((widget, index) => createCanvasItemFromWidget(widget, index));

const createCanvasItem = (
  template: BuilderCardTemplate,
  id: number,
  position: Pick<BuilderCanvasItem, "x" | "y">,
): BuilderCanvasItem => ({
  i: `builder-card-${id}`,
  templateId: template.id,
  label: template.label,
  category: template.category,
  description: template.description,
  titleOverride: null,
  savedFilters: {},
  isVisible: true,
  // accent: template.accent,
  x: position.x,
  y: position.y,
  w: template.grid.w,
  h: template.grid.h,
  minW: template.grid.minW,
  minH: template.grid.minH,
  maxW: template.grid.maxW,
  maxH: template.grid.maxH,
  assignedWidgetId: null,
  persistedWidgetId: null,
  isServerWidgetAssignment: false,
});

const INITIAL_CANVAS_ITEMS: BuilderCanvasItem[] = [];

const INITIAL_NEXT_CANVAS_ITEM_ID = 1;

const getSlotInfoFromCanvasItems = (
  canvasItems: BuilderCanvasItem[],
): BuilderSlotInfo =>
  buildSlotInfo(
    canvasItems.length,
    canvasItems.filter((item) => item.assignedWidgetId !== null).length,
  );

const sortCanvasItemsForSaveDraft = (
  canvasItems: BuilderCanvasItem[],
): BuilderCanvasItem[] =>
  canvasItems
    .slice()
    .sort(
      (left, right) =>
        left.y - right.y || left.x - right.x || left.i.localeCompare(right.i),
    );

const buildSaveLayoutDraft = (
  selectedDashboardCode: string | null,
  canvasItems: BuilderCanvasItem[],
): BuilderSaveLayoutDraft => ({
  dashboard_code: selectedDashboardCode,
  items: sortCanvasItemsForSaveDraft(canvasItems).map((item, index) => ({
    item_id: item.i,
    slot_template_id: item.templateId,
    assigned_widget_id: item.assignedWidgetId,
    label: item.label,
    category: item.category,
    description: item.description,
    title_override: item.titleOverride,
    saved_filters: item.savedFilters,
    is_visible: item.isVisible,
    chart_type: item.category,
    position: index,
    grid: {
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
    },
    grid_constraints: {
      min_w: item.minW,
      min_h: item.minH,
      max_w: item.maxW,
      max_h: item.maxH,
    },
  })),
});

const syncCanvasItemsWithLayout = (
  currentItems: BuilderCanvasItem[],
  nextLayout: Layout,
): BuilderCanvasItem[] => {
  const layoutById = new Map(
    nextLayout.map((layoutItem) => [layoutItem.i, layoutItem]),
  );
  let hasChanges = false;

  const nextItems = currentItems.map((item) => {
    const updatedItem = layoutById.get(item.i);

    if (!updatedItem) {
      return item;
    }

    const nextItem = {
      ...item,
      x: updatedItem.x,
      y: updatedItem.y,
      w: updatedItem.w,
      h: updatedItem.h,
    };

    if (
      nextItem.x !== item.x ||
      nextItem.y !== item.y ||
      nextItem.w !== item.w ||
      nextItem.h !== item.h
    ) {
      hasChanges = true;
    }

    return nextItem;
  });

  return hasChanges ? nextItems : currentItems;
};

export const useBuilderStore = create<BuilderStore>((set) => ({
  currentDashboard: null,
  selectedDashboardCode: null,
  dashboardTemplates: [],
  slotInfo: getSlotInfoFromCanvasItems(INITIAL_CANVAS_ITEMS),
  canvasItems: INITIAL_CANVAS_ITEMS,
  saveLayoutDraft: EMPTY_SAVE_LAYOUT_DRAFT,
  pendingWidgetDeletionIds: [],
  nextCanvasItemId: INITIAL_NEXT_CANVAS_ITEM_ID,
  draggingWidgetId: null,

  setCurrentDashboard: (currentDashboard) =>
    set((state) => ({
      currentDashboard,
      saveLayoutDraft: buildSaveLayoutDraft(
        currentDashboard?.dashboard.code ?? state.selectedDashboardCode,
        state.canvasItems,
      ),
      pendingWidgetDeletionIds: [],
    })),

  setSelectedDashboardCode: (selectedDashboardCode) =>
    set((state) => ({
      selectedDashboardCode,
      saveLayoutDraft: buildSaveLayoutDraft(
        selectedDashboardCode,
        state.canvasItems,
      ),
      pendingWidgetDeletionIds: [],
    })),

  setDashboardTemplates: (dashboardTemplates) => set({ dashboardTemplates }),

  setSlotInfo: ({ totalSlots, filledSlots }) =>
    set({ slotInfo: buildSlotInfo(totalSlots, filledSlots) }),

  setDraggingWidgetId: (draggingWidgetId) => set({ draggingWidgetId }),

  addCanvasItem: (template) =>
    set((state) => {
      const { canvasItems, nextCanvasItemId, selectedDashboardCode } = state;
      const nextRow = canvasItems.reduce(
        (maxRow, item) => Math.max(maxRow, item.y + item.h),
        0,
      );
      const nextCanvasItems = [
        ...canvasItems,
        createCanvasItem(template, nextCanvasItemId, { x: 0, y: nextRow }),
      ];

      return {
        canvasItems: nextCanvasItems,
        saveLayoutDraft: buildSaveLayoutDraft(
          selectedDashboardCode,
          nextCanvasItems,
        ),
        nextCanvasItemId: nextCanvasItemId + 1,
        slotInfo: getSlotInfoFromCanvasItems(nextCanvasItems),
      };
    }),

  addWidgetCanvasItem: (widgetId) =>
    set((state) => {
      const assignedTemplate = state.dashboardTemplates.find(
        (widget) => widget.id === widgetId,
      );

      if (!assignedTemplate) {
        return state;
      }

      const { canvasItems, nextCanvasItemId, selectedDashboardCode } = state;
      const nextRow = canvasItems.reduce(
        (maxRow, item) => Math.max(maxRow, item.y + item.h),
        0,
      );
      const defaultGridW = assignedTemplate.defaultGridW || 6;
      const defaultGridH = assignedTemplate.defaultGridH || 12;
      const minGridW = assignedTemplate.minGridW || 2;
      const minGridH = assignedTemplate.minGridH || 2;
      const maxGridW = assignedTemplate.maxGridW || 24;
      const maxGridH = assignedTemplate.maxGridH || 24;
      const nextCanvasItem: BuilderCanvasItem = {
        i: `builder-card-${nextCanvasItemId}`,
        templateId: String(assignedTemplate.templateId),
        label: assignedTemplate.title,
        category: assignedTemplate.chartType,
        description: assignedTemplate.description,
        titleOverride: null,
        savedFilters: {},
        isVisible: true,
        x: 0,
        y: nextRow,
        w: clampBetween(defaultGridW, minGridW, maxGridW),
        h: clampBetween(defaultGridH, minGridH, maxGridH),
        minW: minGridW,
        minH: minGridH,
        maxW: maxGridW,
        maxH: maxGridH,
        assignedWidgetId: assignedTemplate.id,
        persistedWidgetId: null,
        isServerWidgetAssignment: false,
      };
      const nextCanvasItems = [...canvasItems, nextCanvasItem];

      return {
        canvasItems: nextCanvasItems,
        saveLayoutDraft: buildSaveLayoutDraft(
          selectedDashboardCode,
          nextCanvasItems,
        ),
        nextCanvasItemId: nextCanvasItemId + 1,
        slotInfo: getSlotInfoFromCanvasItems(nextCanvasItems),
      };
    }),

  updateCanvasItemWidgetConfig: (itemId, config) =>
    set((state) => {
      let hasChanges = false;

      const nextCanvasItems = state.canvasItems.map((item) => {
        if (item.i !== itemId) {
          return item;
        }

        const nextTitleOverride =
          config.titleOverride !== undefined
            ? config.titleOverride
            : item.titleOverride;
        const nextSavedFilters =
          config.savedFilters !== undefined
            ? config.savedFilters
            : item.savedFilters;

        if (
          nextTitleOverride === item.titleOverride &&
          nextSavedFilters === item.savedFilters
        ) {
          return item;
        }

        hasChanges = true;

        return {
          ...item,
          titleOverride: nextTitleOverride,
          savedFilters: nextSavedFilters,
        };
      });

      if (!hasChanges) {
        return state;
      }

      return {
        canvasItems: nextCanvasItems,
        saveLayoutDraft: buildSaveLayoutDraft(
          state.selectedDashboardCode,
          nextCanvasItems,
        ),
      };
    }),

  queueWidgetDeletion: (widgetId) =>
    set((state) => {
      if (!widgetId) {
        return state;
      }

      if (state.pendingWidgetDeletionIds.includes(widgetId)) {
        return state;
      }

      return {
        pendingWidgetDeletionIds: [...state.pendingWidgetDeletionIds, widgetId],
      };
    }),

  clearPendingWidgetDeletions: () =>
    set(() => ({
      pendingWidgetDeletionIds: [],
    })),

  removeCanvasItem: (itemId) =>
    set((state) => {
      const { canvasItems } = state;
      const nextCanvasItems = canvasItems.filter((item) => item.i !== itemId);

      if (nextCanvasItems.length === canvasItems.length) {
        return state;
      }

      return {
        canvasItems: nextCanvasItems,
        saveLayoutDraft: buildSaveLayoutDraft(
          state.selectedDashboardCode,
          nextCanvasItems,
        ),
        slotInfo: getSlotInfoFromCanvasItems(nextCanvasItems),
      };
    }),

  commitCanvasLayout: (layout) =>
    set((state) => {
      const { canvasItems } = state;
      const nextCanvasItems = syncCanvasItemsWithLayout(canvasItems, layout);

      if (nextCanvasItems === canvasItems) {
        return state;
      }

      return {
        canvasItems: nextCanvasItems,
        saveLayoutDraft: buildSaveLayoutDraft(
          state.selectedDashboardCode,
          nextCanvasItems,
        ),
      };
    }),

  swapCanvasItems: (sourceItemId, targetItemId) =>
    set((state) => {
      if (sourceItemId === targetItemId) {
        return state;
      }

      const sourceItem = state.canvasItems.find(
        (item) => item.i === sourceItemId,
      );
      const targetItem = state.canvasItems.find(
        (item) => item.i === targetItemId,
      );

      if (!sourceItem || !targetItem) {
        return state;
      }

      const nextCanvasItems = state.canvasItems.map((item) => {
        if (item.i === sourceItemId) {
          return {
            ...item,
            x: targetItem.x,
            y: targetItem.y,
            w: targetItem.w,
            h: targetItem.h,
            minW: targetItem.minW,
            minH: targetItem.minH,
            maxW: targetItem.maxW,
            maxH: targetItem.maxH,
          };
        }

        if (item.i === targetItemId) {
          return {
            ...item,
            x: sourceItem.x,
            y: sourceItem.y,
            w: targetItem.w,
            h: targetItem.h,
            minW: targetItem.minW,
            minH: targetItem.minH,
            maxW: targetItem.maxW,
            maxH: targetItem.maxH,
          };
        }

        return item;
      });

      return {
        canvasItems: nextCanvasItems,
        saveLayoutDraft: buildSaveLayoutDraft(
          state.selectedDashboardCode,
          nextCanvasItems,
        ),
      };
    }),

  assignWidgetToCanvasItem: (itemId, widgetId) =>
    set((state) => {
      const assignedTemplate = state.dashboardTemplates.find(
        (widget) => widget.id === widgetId,
      );
      let hasChanges = false;

      const nextCanvasItems = state.canvasItems.map((item) => {
        if (item.i !== itemId) {
          return item;
        }

        const nextMinW = assignedTemplate?.minGridW ?? item.minW;
        const nextMinH = assignedTemplate?.minGridH ?? item.minH;
        const nextMaxW = assignedTemplate?.maxGridW ?? item.maxW;
        const nextMaxH = assignedTemplate?.maxGridH ?? item.maxH;
        const nextW = clampBetween(item.w, nextMinW, nextMaxW);
        const nextH = clampBetween(item.h, nextMinH, nextMaxH);
        const nextLabel = assignedTemplate?.title ?? item.label;
        const nextCategory = assignedTemplate?.chartType ?? item.category;
        const nextDescription =
          assignedTemplate?.description ?? item.description;
        const nextTemplateId = assignedTemplate
          ? String(assignedTemplate.templateId)
          : item.templateId;

        if (
          item.assignedWidgetId === widgetId &&
          item.label === nextLabel &&
          item.category === nextCategory &&
          item.description === nextDescription &&
          item.templateId === nextTemplateId &&
          item.minW === nextMinW &&
          item.minH === nextMinH &&
          item.maxW === nextMaxW &&
          item.maxH === nextMaxH &&
          item.w === nextW &&
          item.h === nextH
        ) {
          return item;
        }

        hasChanges = true;

        return {
          ...item,
          templateId: nextTemplateId,
          label: nextLabel,
          category: nextCategory,
          description: nextDescription,
          assignedWidgetId: widgetId,
          persistedWidgetId: null,
          isServerWidgetAssignment: false,
          minW: nextMinW,
          minH: nextMinH,
          maxW: nextMaxW,
          maxH: nextMaxH,
          w: nextW,
          h: nextH,
        };
      });

      if (!hasChanges) {
        return state;
      }

      return {
        canvasItems: nextCanvasItems,
        saveLayoutDraft: buildSaveLayoutDraft(
          state.selectedDashboardCode,
          nextCanvasItems,
        ),
        slotInfo: getSlotInfoFromCanvasItems(nextCanvasItems),
      };
    }),

  resetCanvas: () =>
    set((state) => ({
      canvasItems: INITIAL_CANVAS_ITEMS,
      saveLayoutDraft: buildSaveLayoutDraft(
        state.selectedDashboardCode,
        INITIAL_CANVAS_ITEMS,
      ),
      nextCanvasItemId: INITIAL_NEXT_CANVAS_ITEM_ID,
      draggingWidgetId: null,
      slotInfo: getSlotInfoFromCanvasItems(INITIAL_CANVAS_ITEMS),
      pendingWidgetDeletionIds: [],
    })),

  syncDashboard: (currentDashboard, totalSlots) =>
    set(() => {
      if (!currentDashboard) {
        return {
          currentDashboard: null,
          selectedDashboardCode: null,
          slotInfo: EMPTY_SLOT_INFO,
          canvasItems: INITIAL_CANVAS_ITEMS,
          saveLayoutDraft: EMPTY_SAVE_LAYOUT_DRAFT,
          nextCanvasItemId: INITIAL_NEXT_CANVAS_ITEM_ID,
        };
      }

      const nextCanvasItems = createCanvasItemsFromWidgets(
        currentDashboard.widgets ?? [],
      );

      return {
        currentDashboard,
        selectedDashboardCode: currentDashboard.dashboard.code ?? null,
        slotInfo: getSlotInfoFromWidgets(
          currentDashboard.widgets ?? [],
          totalSlots,
        ),
        canvasItems: nextCanvasItems,
        saveLayoutDraft: buildSaveLayoutDraft(
          currentDashboard.dashboard.code ?? null,
          nextCanvasItems,
        ),
        nextCanvasItemId: nextCanvasItems.length + 1,
        pendingWidgetDeletionIds: [],
      };
    }),

  setSlotsFromWidgets: (widgets, totalSlots) =>
    set({ slotInfo: getSlotInfoFromWidgets(widgets, totalSlots) }),

  resetBuilderStore: () =>
    set({
      currentDashboard: null,
      selectedDashboardCode: null,
      dashboardTemplates: [],
      slotInfo: getSlotInfoFromCanvasItems(INITIAL_CANVAS_ITEMS),
      canvasItems: INITIAL_CANVAS_ITEMS,
      saveLayoutDraft: EMPTY_SAVE_LAYOUT_DRAFT,
      pendingWidgetDeletionIds: [],
      nextCanvasItemId: INITIAL_NEXT_CANVAS_ITEM_ID,
      draggingWidgetId: null,
    }),
}));

export default useBuilderStore;
